
import { useState, useCallback, useEffect } from 'react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'

interface Orcamento {
  _id: string
  numero: string
  leadId?: string
  nomeCliente: string
  contatoCliente: string
  nomeProjeto: string
  descricao: string
  status: 'rascunho' | 'enviado' | 'aprovado' | 'perdido' | 'expirado'
  dataEmissao: string
  dataValidade: string
  bdi: number
  desconto: number
  etapas: Etapa[]
  valorSubtotal: number
  valorTotal: number
  observacoes?: string
  templateId?: string
  creator: string
  createdAt: string
  updatedAt: string
}

interface Etapa {
  codigo: string
  nome: string
  subetapas: Subetapa[]
}

interface Subetapa {
  codigo: string
  nome: string
  itens: ItemOrcamento[]
}

interface ItemOrcamento {
  tipo: 'insumo' | 'composicao'
  itemId: string
  nome: string
  unidadeMedida: string
  quantidade: number
  precoUnitario: number
  subtotal: number
}

interface TemplateOrcamento {
  _id: string
  nome: string
  descricao: string
  categoria: string
  bdi: number
  etapas: Etapa[]
  ativo: boolean
}

interface HistoricoOrcamento {
  _id: string
  orcamentoId: string
  acao: string
  descricao: string
  valorAnterior?: number
  valorNovo?: number
  statusAnterior?: string
  statusNovo?: string
  dataAcao: string
  usuario: string
  observacoes?: string
}

interface Notificacao {
  _id: string
  tipo: 'lead_fechado' | 'orcamento_status' | 'orcamento_expirado' | 'sistema'
  titulo: string
  mensagem: string
  leadId?: string
  orcamentoId?: string
  lida: boolean
  dataNotificacao: string
  usuario: string
}

interface OrcamentoMetrics {
  totalOrcamentos: number
  totalOrcamentosAnterior: number
  valorTotal: number
  valorTotalAnterior: number
  quantidadeAprovada: number
  quantidadeAprovadaAnterior: number
  quantidadeEnviada: number
  quantidadeEnviadaAnterior: number
}

export const useOrcamentos = () => {
  const [orcamentos, setOrcamentos] = useState<Orcamento[]>([])
  const [templates, setTemplates] = useState<TemplateOrcamento[]>([])
  const [historico, setHistorico] = useState<HistoricoOrcamento[]>([])
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([])
  const [loading, setLoading] = useState(false)

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [orcamentosData, templatesData, historicoData, notificacoesData] = await Promise.all([
        lumi.entities.orcamentos.list(),
        lumi.entities.templates_orcamento.list(),
        lumi.entities.historico_orcamentos.list(),
        lumi.entities.notificacoes.list()
      ])

      setOrcamentos(orcamentosData.list || [])
      setTemplates(templatesData.list || [])
      setHistorico(historicoData.list || [])
      setNotificacoes(notificacoesData.list || [])
    } catch (error: any) {
      console.error('Erro ao carregar dados de orçamentos:', error)
      toast.error('Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }, [])

  // Calculate metrics with comparison to previous month
  const getMetrics = (): OrcamentoMetrics => {
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    
    const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1
    const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear

    const currentMonthOrcamentos = orcamentos.filter(orc => {
      const date = new Date(orc.createdAt)
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear
    })

    const previousMonthOrcamentos = orcamentos.filter(orc => {
      const date = new Date(orc.createdAt)
      return date.getMonth() === previousMonth && date.getFullYear() === previousYear
    })

    return {
      totalOrcamentos: currentMonthOrcamentos.length,
      totalOrcamentosAnterior: previousMonthOrcamentos.length,
      valorTotal: currentMonthOrcamentos.reduce((sum, orc) => sum + orc.valorTotal, 0),
      valorTotalAnterior: previousMonthOrcamentos.reduce((sum, orc) => sum + orc.valorTotal, 0),
      quantidadeAprovada: currentMonthOrcamentos.filter(orc => orc.status === 'aprovado').length,
      quantidadeAprovadaAnterior: previousMonthOrcamentos.filter(orc => orc.status === 'aprovado').length,
      quantidadeEnviada: currentMonthOrcamentos.filter(orc => orc.status === 'enviado').length,
      quantidadeEnviadaAnterior: previousMonthOrcamentos.filter(orc => orc.status === 'enviado').length
    }
  }

  // Generate unique budget number
  const generateBudgetNumber = () => {
    const year = new Date().getFullYear()
    const count = orcamentos.length + 1
    return `ORC-${year}-${count.toString().padStart(3, '0')}`
  }

  // Create orçamento
  const createOrcamento = async (data: Omit<Orcamento, '_id' | 'numero' | 'createdAt' | 'updatedAt'>) => {
    try {
      const numero = generateBudgetNumber()
      const now = new Date().toISOString()
      
      const orcamentoData = {
        ...data,
        numero,
        bdi: Number(data.bdi || 0),
        desconto: Number(data.desconto || 0),
        valorSubtotal: Number(data.valorSubtotal || 0),
        valorTotal: Number(data.valorTotal || 0),
        creator: 'admin',
        createdAt: now,
        updatedAt: now
      }

      const newOrcamento = await lumi.entities.orcamentos.create(orcamentoData)

      // Create history entry
      await lumi.entities.historico_orcamentos.create({
        orcamentoId: newOrcamento._id,
        acao: 'criado',
        descricao: `Orçamento ${numero} criado${data.leadId ? ' a partir do CRM' : ''}`,
        dataAcao: now,
        usuario: 'Admin Sistema',
        creator: 'admin',
        createdAt: now
      })

      await fetchData()
      toast.success('Orçamento criado com sucesso!')
      return newOrcamento
    } catch (error: any) {
      console.error('Erro ao criar orçamento:', error)
      toast.error('Erro ao criar orçamento')
      throw error
    }
  }

  // Update orçamento
  const updateOrcamento = async (id: string, data: Partial<Orcamento>) => {
    try {
      const orcamentoAtual = orcamentos.find(o => o._id === id)
      if (!orcamentoAtual) throw new Error('Orçamento não encontrado')

      const now = new Date().toISOString()
      const updateData = {
        ...data,
        updatedAt: now
      }

      await lumi.entities.orcamentos.update(id, updateData)

      // Create history entry for significant changes
      if (data.status && data.status !== orcamentoAtual.status) {
        await lumi.entities.historico_orcamentos.create({
          orcamentoId: id,
          acao: data.status,
          descricao: `Status alterado de ${orcamentoAtual.status} para ${data.status}`,
          statusAnterior: orcamentoAtual.status,
          statusNovo: data.status,
          dataAcao: now,
          usuario: 'Admin Sistema',
          creator: 'admin',
          createdAt: now
        })

        // Create notification for status change
        await lumi.entities.notificacoes.create({
          tipo: 'orcamento_status',
          titulo: 'Status do Orçamento Alterado',
          mensagem: `Orçamento ${orcamentoAtual.numero} mudou para ${data.status}`,
          orcamentoId: id,
          lida: false,
          dataNotificacao: now,
          usuario: 'admin',
          creator: 'sistema',
          createdAt: now
        })
      }

      if (data.valorTotal && data.valorTotal !== orcamentoAtual.valorTotal) {
        await lumi.entities.historico_orcamentos.create({
          orcamentoId: id,
          acao: 'editado',
          descricao: 'Valor do orçamento alterado',
          valorAnterior: orcamentoAtual.valorTotal,
          valorNovo: data.valorTotal,
          dataAcao: now,
          usuario: 'Admin Sistema',
          creator: 'admin',
          createdAt: now
        })
      }

      await fetchData()
      toast.success('Orçamento atualizado com sucesso!')
    } catch (error: any) {
      console.error('Erro ao atualizar orçamento:', error)
      toast.error('Erro ao atualizar orçamento')
      throw error
    }
  }

  // Delete orçamento
  const deleteOrcamento = async (id: string) => {
    try {
      await lumi.entities.orcamentos.delete(id)
      await fetchData()
      toast.success('Orçamento excluído com sucesso!')
    } catch (error: any) {
      console.error('Erro ao excluir orçamento:', error)
      toast.error('Erro ao excluir orçamento')
      throw error
    }
  }

  // Create template
  const createTemplate = async (data: Omit<TemplateOrcamento, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString()
      const templateData = {
        ...data,
        bdi: Number(data.bdi || 0),
        creator: 'admin',
        createdAt: now,
        updatedAt: now
      }

      await lumi.entities.templates_orcamento.create(templateData)
      await fetchData()
      toast.success('Template criado com sucesso!')
    } catch (error: any) {
      console.error('Erro ao criar template:', error)
      toast.error('Erro ao criar template')
      throw error
    }
  }

  // Update template
  const updateTemplate = async (id: string, data: Partial<TemplateOrcamento>) => {
    try {
      const updateData = {
        ...data,
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.templates_orcamento.update(id, updateData)
      await fetchData()
      toast.success('Template atualizado com sucesso!')
    } catch (error: any) {
      console.error('Erro ao atualizar template:', error)
      toast.error('Erro ao atualizar template')
      throw error
    }
  }

  // Delete template
  const deleteTemplate = async (id: string) => {
    try {
      await lumi.entities.templates_orcamento.delete(id)
      await fetchData()
      toast.success('Template excluído com sucesso!')
    } catch (error: any) {
      console.error('Erro ao excluir template:', error)
      toast.error('Erro ao excluir template')
      throw error
    }
  }

  // Mark notification as read
  const markNotificationAsRead = async (id: string) => {
    try {
      await lumi.entities.notificacoes.update(id, { lida: true })
      await fetchData()
    } catch (error: any) {
      console.error('Erro ao marcar notificação como lida:', error)
    }
  }

  // Get leads fechados for budget generation
  const getLeadsFechados = async () => {
    try {
      const { list } = await lumi.entities.leads.list()
      return list.filter(lead => lead.status === 'fechado')
    } catch (error: any) {
      console.error('Erro ao buscar leads fechados:', error)
      return []
    }
  }

  // Calculate totals for etapas
  const calculateEtapaTotal = (etapa: Etapa) => {
    return etapa.subetapas.reduce((total, subetapa) => {
      return total + subetapa.itens.reduce((subtotal, item) => subtotal + item.subtotal, 0)
    }, 0)
  }

  // Calculate material and labor breakdown
  const calculateMaterialLabor = async (etapa: Etapa) => {
    let materialTotal = 0
    let laborTotal = 0

    try {
      // Get insumos data to check types
      const { list: insumos } = await lumi.entities.insumos.list()
      
      for (const subetapa of etapa.subetapas) {
        for (const item of subetapa.itens) {
          if (item.tipo === 'insumo') {
            const insumo = insumos.find(i => i._id === item.itemId)
            if (insumo) {
              if (insumo.tipo === 'material') {
                materialTotal += item.subtotal
              } else if (insumo.tipo === 'mao_obra') {
                laborTotal += item.subtotal
              }
            }
          }
          // For composições, we'd need to break down their components
        }
      }
    } catch (error) {
      console.error('Erro ao calcular material/mão de obra:', error)
    }

    return { materialTotal, laborTotal }
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    orcamentos,
    templates,
    historico,
    notificacoes,
    loading,
    metrics: getMetrics(),
    fetchData,
    createOrcamento,
    updateOrcamento,
    deleteOrcamento,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    markNotificationAsRead,
    getLeadsFechados,
    calculateEtapaTotal,
    calculateMaterialLabor,
    generateBudgetNumber
  }
}
